import React, { useEffect, useMemo, useState } from 'react';
import { PANELS, PANELS_TYPES } from 'app/containers/MainDashboard/consts.js';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner.jsx';
import classNames from 'classnames';
import { useMainDashboardContext } from 'app/containers/MainDashboard/contexts/mainDashboardContext.jsx';
import useForecastData from 'app/containers/MainDashboard/hooks/react-query/useForecastData.js';
import { getFirstAndLastDayOfPreviousMonth, sumTotalCost } from 'app/containers/MainDashboard/utils.js';
import useCueData from 'app/containers/MainDashboard/hooks/react-query/useCueData.js';
import { useRootStore } from 'app/contexts/RootStoreContext.jsx';
import ForecastSpecialInfo from 'app/containers/MainDashboard/panels/HistoryWithForecast/ForecastSpecialInfo.jsx';
import UsageCost from '../components/UsageCost.jsx';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext.jsx';
import styles from 'app/containers/MainDashboard/panels/costPanels.module.scss';

const ForecastedCost = ({ currentOverrideCurrency, withOverrideApiKey }) => {
  const {
    updatePanelLoadingState,
    getDynamicFilters,
    filters,
    getOverrideAccount,
    forecastEmptyStateText,
    forecastEmptyStateFiltersText,
  } = useMainDashboardContext();
  const [forecastPercent, setForecastPercent] = useState();
  const { appStore, usersStore } = useRootStore();
  const forecastHook = useForecastData(!forecastEmptyStateFiltersText && !appStore.isChangeAccountLoading);
  const cueHook = useCueData(!appStore.isChangeAccountLoading);
  const currencyContext = useCurrencyContext();

  const displayComingSoon = useMemo(() => {
    if (!withOverrideApiKey?.accountKey) {
      return !usersStore.isCurrentAccountSingle && !appStore.isKeyCloakManagement;
    }
    return !usersStore.isAccountSingle(withOverrideApiKey?.accountKey) && !appStore.isKeyCloakManagement;
  }, [withOverrideApiKey?.accountKey, usersStore, appStore.isKeyCloakManagement]);

  const { data: forecastData, isLoading } = forecastHook.fetchForecastDataTotal(
    1,
    filters.cost?.[0]?.value,
    filters.exclude?.map((f) => f.label),
    appStore.isPpApplied,
    withOverrideApiKey,
  );
  const overrideAccount = getOverrideAccount(withOverrideApiKey?.accountKey);
  const forecastDisplayedValue = useMemo(() => {
    if (currencyContext?.active) {
      if (overrideAccount && overrideAccount?.processingCurrency !== overrideAccount?.providerCurrency) {
        return Number(forecastData?.forecastTotal) * Number(overrideAccount?.exchangeRate);
      }
      if (currencyContext.currencyInUse?.code !== currencyContext.providerCurrency) {
        return Number(forecastData?.forecastTotal) * Number(currencyContext?.exchangeRate);
      }
    }
    return forecastData?.forecastTotal;
  }, [
    forecastData?.forecastTotal,
    currencyContext?.exchangeRate,
    currencyContext?.active,
    overrideAccount,
    currencyContext.currencyInUse?.code,
    currencyContext.providerCurrency,
  ]);

  const previousMonthParams = {
    ...PANELS[PANELS_TYPES.PREVIOUS_MONTH_COST].params,
    ...getFirstAndLastDayOfPreviousMonth(),
    ...getDynamicFilters(),
    isPpApplied: appStore.isPpApplied,
    withOverrideApiKey,
  };
  const { data: previousMonthData, isLoading: isPreviousMonthData } = cueHook.fetchCueData(previousMonthParams);

  useEffect(() => {
    updatePanelLoadingState(PANELS_TYPES.FORECASTED_MONTHLY_COST, isLoading || isPreviousMonthData);
  }, [isLoading, isPreviousMonthData, updatePanelLoadingState]);

  useEffect(() => {
    if (!forecastData || !previousMonthData) {
      return;
    }
    const totalPreviousMonth = sumTotalCost(previousMonthData);
    const percent = totalPreviousMonth
      ? ((forecastData?.forecastTotal - totalPreviousMonth) * 100) / totalPreviousMonth
      : null;
    setForecastPercent(percent);
  }, [forecastData, previousMonthData]);

  return (
    <div className={classNames(styles.costPanel, styles.noHover)}>
      {isLoading ? (
        <Spinner className={styles.spinner} size={SPINNER_SIZES.MEDIUM} />
      ) : (
        <UsageCost
          title={PANELS[PANELS_TYPES.FORECASTED_MONTHLY_COST].title}
          value={forecastDisplayedValue}
          specialInfo={<ForecastSpecialInfo />}
          showPercent={!forecastEmptyStateText && !forecastEmptyStateFiltersText}
          percent={forecastPercent ? `${forecastPercent}` : null}
          displayComingSoon={displayComingSoon}
          emptyStateText={forecastEmptyStateText || forecastEmptyStateFiltersText}
          overrideCurrency={currentOverrideCurrency}
        />
      )}
    </div>
  );
};
export default ForecastedCost;
